import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { validateRequiredDocuments } from "@/services/documentValidationService";
import { followerAutomationService } from "@/services/followerAutomationService";
import { moveProjectToPipelineStage, type MovableProject } from "@/services/projectMoveService";
import { Stage } from "@/types/pipeline";
import { ExtendedProject } from "@/types/project";

// ============================================================================
// Types
// ============================================================================

/** Options for the useProjectStateManagement hook */
export interface UseProjectStateManagementOptions {
  projectId: string | null;
  project: ExtendedProject | null;
  /** Callback to update project state in parent */
  setProject: React.Dispatch<React.SetStateAction<ExtendedProject | null>>;
  /** Optional callback fired on status change */
  onStatusChange?: (oldStatus: string, newStatus: string) => void;
  /** Optional callback fired on stage change */
  onStageChange?: (oldStageId: number, newStageId: number) => void;
  /** Optional callback fired on pipeline change */
  onPipelineChange?: (oldPipelineId: number, newPipelineId: number) => void;
  /** Optional error callback */
  onError?: (error: Error) => void;
  /** Optional callback to set saving state in parent */
  setSaving?: (saving: boolean) => void;
  /** Optional callback to set save error in parent */
  setSaveError?: (error: string | null) => void;
  /** Optional callback to set save success in parent */
  setSaveSuccess?: (success: boolean) => void;
}

/** Return type for the useProjectStateManagement hook */
export interface UseProjectStateManagementReturn {
  // Dialog visibility states
  showStageDialog: boolean;
  showStatusDialog: boolean;

  // Dialog setters (with React.SetStateAction support for backward compatibility)
  setShowStageDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setShowStatusDialog: React.Dispatch<React.SetStateAction<boolean>>;

  // Dropdown states (for inline stage/status selectors)
  stageDropdownOpen: boolean;
  statusDropdownOpen: boolean;
  setStageDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setStatusDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  stageDropdownRef: React.RefObject<HTMLDivElement | null>;
  statusDropdownRef: React.RefObject<HTMLDivElement | null>;
  stageDropdownPosition: { top: number; left: number; width: number };
  setStageDropdownPosition: React.Dispatch<React.SetStateAction<{ top: number; left: number; width: number }>>;

  // Data
  stages: Stage[];
  setStages: React.Dispatch<React.SetStateAction<Stage[]>>;

  // Operations
  handleStatusChange: (status: string) => Promise<void>;
  handleStageChange: (stageId: number) => Promise<void>;
  /** Moves to a stage in any pipeline the project may move to, including its own. */
  handleMoveToPipelineStage: (pipelineId: number, stageId: number) => Promise<void>;

  // Data fetching
  fetchStages: () => Promise<void>;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProjectStateManagement({
  projectId,
  project,
  setProject,
  onStatusChange,
  onStageChange,
  onPipelineChange,
  onError,
  setSaving,
  setSaveError,
  setSaveSuccess,
}: UseProjectStateManagementOptions): UseProjectStateManagementReturn {
  const { user } = useAuth();
  const { roleInfo } = usePermissions();
  const { toast } = useToast();

  // ============================================================================
  // Dialog States
  // ============================================================================

  const [showStageDialog, setShowStageDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);

  // ============================================================================
  // Dropdown States (for inline selectors in header)
  // ============================================================================

  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const stageDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [stageDropdownPosition, setStageDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });

  // ============================================================================
  // Data States
  // ============================================================================

  const [stages, setStages] = useState<Stage[]>([]);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  /** Fetch stages for current pipeline */
  const fetchStages = useCallback(async () => {
    if (!project?.pipeline_id) return;

    try {
      const { data: stagesData, error: stagesError } = await supabase
        .from("stages")
        .select("*")
        .eq("pipeline_id", project.pipeline_id)
        .order("stage_order", { ascending: true });

      if (stagesError) throw stagesError;

      setStages(stagesData || []);
    } catch (error) {
      console.error("Error fetching stages:", error);
      onError?.(error as Error);
    }
  }, [project?.pipeline_id, onError]);


  // ============================================================================
  // Effects
  // ============================================================================

  // Fetch stages when pipeline changes
  useEffect(() => {
    if (project?.pipeline_id) {
      fetchStages();
    }
  }, [project?.pipeline_id, fetchStages]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setStatusDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update stage dropdown position when opened
  useEffect(() => {
    if (stageDropdownOpen && stageDropdownRef.current) {
      const rect = stageDropdownRef.current.getBoundingClientRect();
      setStageDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [stageDropdownOpen]);

  // ============================================================================
  // Status Change Handler
  // ============================================================================

  const handleStatusChange = useCallback(async (status: string) => {
    if (!projectId) return;

    try {
      const oldStatus = project?.project_status || "";

      // If setting to Cancelled, also move project to the Cancelled stage (if one exists)
      const updates: Record<string, unknown> = { project_status: status };

      if (status === "Cancelled" && stages.length > 0) {
        const cancelledStage = stages.find(
          (s) => s.name.toLowerCase() === "cancelled"
        );
        if (cancelledStage && cancelledStage.id !== project?.current_stage_id) {
          updates.current_stage_id = cancelledStage.id;
          updates.stage_changed_at = new Date().toISOString();
        }
      }

      const { error } = await supabase
        .from("projects")
        .update(updates)
        .eq("id", projectId);

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
        onError?.(new Error(error.message));
      } else {
        const movedToStage = updates.current_stage_id
          ? stages.find((s) => s.id === updates.current_stage_id)?.name
          : null;
        toast({
          title: "Success",
          description: movedToStage
            ? `Project status updated and moved to ${movedToStage} stage.`
            : "Project status updated.",
        });

        // Update local state
        setProject((prev) =>
          prev
            ? {
                ...prev,
                project_status: status,
                ...(updates.current_stage_id
                  ? {
                      current_stage_id: updates.current_stage_id as number,
                      stage_changed_at: updates.stage_changed_at as string,
                    }
                  : {}),
              }
            : null
        );

        // Dispatch a custom event to notify the pipeline to refresh
        const refreshEvent = new CustomEvent("project-status-updated", {
          detail: { projectId, status },
        });
        document.dispatchEvent(refreshEvent);

        // Fire callback
        onStatusChange?.(oldStatus, status);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update project status.",
        variant: "destructive",
      });
      onError?.(error as Error);
    }
  }, [projectId, project?.project_status, project?.current_stage_id, stages, setProject, toast, onStatusChange, onError]);

  // ============================================================================
  // Stage Change Handler
  // ============================================================================

  const handleStageChange = useCallback(async (stageId: number) => {
    if (!project) {
      toast({
        title: "Error",
        description: "Project data not loaded. Please try refreshing the page.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving?.(true);
      setSaveError?.(null);

      // Get the stage names for logging
      const { data: stagesData, error: stagesError } = await supabase
        .from("stages")
        .select("id, name")
        .in("id", [project.current_stage_id, stageId]);

      if (stagesError) throw stagesError;

      const oldStage = stagesData.find(
        (s) => s.id === project.current_stage_id
      );
      const newStage = stagesData.find((s) => s.id === stageId);

      // Validate required documents before stage advancement (DOC-1.0)
      const validation = await validateRequiredDocuments(
        project.id,
        project.project_type || 'Commercial',
        project.commercial_type
      );

      if (!validation.valid) {
        // Check if user has permission to override validation
        const canOverride = roleInfo && (
          roleInfo.name === 'Admin' ||
          roleInfo.name === 'Superadmin' ||
          roleInfo.name === 'Internal PM' ||
          roleInfo.name === 'Internal PM Manager'
        );

        if (canOverride) {
          // Authorized user: show override confirmation dialog
          const userConfirmed = window.confirm(
            `Missing required documents: ${validation.missingDocuments.join(', ')}\n\n` +
            'This project is missing a power bill required for Back of Meter commercial projects.\n\n' +
            'Do you want to override and continue anyway?'
          );

          if (!userConfirmed) {
            setSaving?.(false);
            return;
          }

          // User confirmed override - log it for audit trail
          console.warn('OVERRIDE: Stage advancement without required documents', {
            projectId: project.id,
            projectName: project.project_name,
            userId: user?.id,
            userRole: roleInfo?.name,
            missingDocuments: validation.missingDocuments,
            timestamp: new Date().toISOString()
          });
        } else {
          // Unauthorized user: block without override option
          toast({
            title: "Missing Required Documents",
            description: `Cannot advance stage: ${validation.missingDocuments.join(', ')} required for Back of Meter projects`,
            variant: "destructive",
          });
          setSaving?.(false);
          return;
        }
      }

      // Update project with stage change and timestamp
      const { error } = await supabase
        .from("projects")
        .update({
          current_stage_id: stageId,
          stage_changed_at: new Date().toISOString(),
          last_updated_by_user: user?.id,
        })
        .eq("id", project.id);

      if (error) throw error;

      // Log the stage change in activity_logs
      const { error: logError } = await supabase.from("activity_logs").insert({
        project_id: project.id,
        user_id: user?.id,
        action_type: "stage_changed",
        details: {
          old_stage: {
            id: project.current_stage_id,
            name: oldStage?.name || "Unknown",
          },
          new_stage: {
            id: stageId,
            name: newStage?.name || "Unknown",
          },
        },
        timestamp: new Date().toISOString(),
        related_entity_type: "stage",
      });

      if (logError) {
        console.error("Error logging stage change:", logError);
      }

      // Update local state
      const updatedStage = stages.find((s) => s.id === stageId);
      if (updatedStage) {
        setProject((prev) =>
          prev
            ? {
                ...prev,
                current_stage_id: stageId,
                stage_changed_at: new Date().toISOString(),
                stages: {
                  id: updatedStage.id,
                  name: updatedStage.name,
                  pipeline_id: updatedStage.pipeline_id,
                  stage_order: updatedStage.stage_order,
                },
              }
            : null
        );
      }

      setSaveSuccess?.(true);
      toast({
        title: "Success",
        description: "Project stage updated successfully.",
      });

      // Dispatch a custom event to notify the pipeline to refresh
      const refreshEvent = new CustomEvent("project-stage-updated", {
        detail: { projectId: project.id, stageId },
      });
      document.dispatchEvent(refreshEvent);

      // Fire callback
      onStageChange?.(project.current_stage_id || 0, stageId);

      // Trigger automations for stage change
      followerAutomationService.onStageChanged(
        project,
        project.current_stage_id || 0,
        stageId
      ).catch(err => {
        console.error('Error executing stage change automations:', err);
      });
    } catch (error) {
      console.error("Error updating project stage:", error);
      setSaveError?.("Failed to update project stage");
      toast({
        title: "Error",
        description: "Failed to update project stage.",
        variant: "destructive",
      });
      onError?.(error as Error);
    } finally {
      setSaving?.(false);
      setStageDropdownOpen(false);
    }
  }, [project, stages, user?.id, roleInfo, setProject, toast, setSaving, setSaveError, setSaveSuccess, onStageChange, onError]);

  // ============================================================================
  // Move To Pipeline + Stage (another pipeline, or a stage in this one)
  // ============================================================================

  const handleMoveToPipelineStage = useCallback(async (pipelineId: number, stageId: number) => {
    if (!project) return;

    const isPipelineChange = pipelineId !== project.pipeline_id;

    try {
      setSaving?.(true);
      setSaveError?.(null);

      const { newStage } = await moveProjectToPipelineStage({
        project: project as unknown as MovableProject,
        toPipelineId: pipelineId,
        toStageId: stageId,
      });

      setProject((prev) =>
        prev
          ? {
              ...prev,
              pipeline_id: pipelineId,
              current_stage_id: stageId,
              stage_changed_at: new Date().toISOString(),
              stages: {
                id: newStage.id,
                name: newStage.name,
                pipeline_id: newStage.pipeline_id,
                stage_order: newStage.stage_order,
              },
            }
          : null
      );

      setSaveSuccess?.(true);
      toast({
        title: "Success",
        description: isPipelineChange
          ? "Project moved to new pipeline successfully."
          : "Project stage updated successfully.",
      });

      if (isPipelineChange) {
        onPipelineChange?.(project.pipeline_id || 0, pipelineId);
      }
      onStageChange?.(project.current_stage_id || 0, stageId);
    } catch (error) {
      console.error("Error updating project:", error);
      setSaveError?.(isPipelineChange ? "Failed to move project to new pipeline" : "Failed to update project stage");
      toast({
        title: "Error",
        description: isPipelineChange ? "Failed to move project to new pipeline." : "Failed to update project stage.",
        variant: "destructive",
      });
      onError?.(error as Error);
    } finally {
      setSaving?.(false);
    }
  }, [project, setProject, toast, setSaving, setSaveError, setSaveSuccess, onStageChange, onPipelineChange, onError]);

  // ============================================================================
  // Return Hook Value
  // ============================================================================

  return {
    // Dialog visibility states
    showStageDialog,
    showStatusDialog,

    // Dialog setters
    setShowStageDialog,
    setShowStatusDialog,

    // Dropdown states
    stageDropdownOpen,
    statusDropdownOpen,
    setStageDropdownOpen,
    setStatusDropdownOpen,
    stageDropdownRef,
    statusDropdownRef,
    stageDropdownPosition,
    setStageDropdownPosition,

    // Data
    stages,
    setStages,

    // Operations
    handleStatusChange,
    handleStageChange,
    handleMoveToPipelineStage,

    // Data fetching
    fetchStages,
  };
}
